import { MessageSquarePlus } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  type FeedbackProjectSearchResult,
  useGiveAdHocFeedback,
  useSearchProjectsForFeedback,
} from "@/hooks/use-adhoc-feedback";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useMyPerson } from "@/hooks/use-persons";
import { useFeedbackSentiments } from "@/hooks/use-review-feedback";

/**
 * Give feedback about any colleague, anytime — EPIC-067 WI-243/244/245 —
 * and, with a project picked, post-project feedback — EPIC-068 WI-250. Same
 * form either way: naming a project just tags the row (AC-716), it doesn't
 * change the shape of what's asked (AC-717 — deliberately short-form, not a
 * full competency set).
 *
 * Open text only: WI-245's "full competency review" option is not built —
 * it would need its own parallel capture path (review_question and
 * review_response both require a formal review_reviewer assignment today),
 * which is a bigger piece of work than this pass covers. This dialog ships
 * the half of WI-245 that has a real yardstick already: a sentiment and the
 * words, same as the anonymous comment on a formal review.
 *
 * `initialSubjectPersonId` pre-fills and locks the colleague picker when
 * opened from that person's own profile; omitted, the picker is open so the
 * dialog also works as the standalone entry point (AC-691).
 */
export function GiveFeedbackDialog({
  initialSubjectPersonId,
  initialProject,
  trigger,
}: {
  initialSubjectPersonId?: string;
  /** Pre-tags the feedback with a project, as the end-of-project prompt does (WI-249). */
  initialProject?: FeedbackProjectSearchResult;
  trigger?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [subjectPersonId, setSubjectPersonId] = useState<string | null>(
    initialSubjectPersonId ?? null,
  );
  const [sentimentKey, setSentimentKey] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [project, setProject] = useState<FeedbackProjectSearchResult | null>(
    initialProject ?? null,
  );
  const [projectSearch, setProjectSearch] = useState("");
  const debouncedProjectSearch = useDebouncedValue(projectSearch, 300);
  const { data: me } = useMyPerson();
  const { data: sentiments } = useFeedbackSentiments(open);
  const { data: projectResults, isLoading: projectsLoading } =
    useSearchProjectsForFeedback(debouncedProjectSearch);
  const give = useGiveAdHocFeedback();

  const reset = () => {
    setSubjectPersonId(initialSubjectPersonId ?? null);
    setSentimentKey(null);
    setBody("");
    setIsAnonymous(false);
    setProject(initialProject ?? null);
    setProjectSearch("");
  };

  const canSubmit = Boolean(subjectPersonId) && Boolean(sentimentKey) && body.trim().length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            <MessageSquarePlus className="mr-1.5 size-3.5" aria-hidden />
            Give Feedback
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Give feedback</DialogTitle>
          <DialogDescription>
            Say something about a colleague anytime — this doesn't wait for a review round.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!initialSubjectPersonId && (
            <div>
              <Label className="mb-1.5 block">Who is this about?</Label>
              <EntityCombobox
                entity="persons"
                value={subjectPersonId}
                onChange={setSubjectPersonId}
                filters={{ status: "active" }}
                excludeIds={me?.id ? [me.id] : undefined}
                placeholder="Search for a colleague…"
              />
            </div>
          )}
          <div>
            <Label className="mb-1.5 block">What kind of feedback is this?</Label>
            <Select value={sentimentKey ?? undefined} onValueChange={setSentimentKey}>
              <SelectTrigger>
                <SelectValue placeholder="Choose one" />
              </SelectTrigger>
              <SelectContent>
                {(sentiments ?? []).map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block" htmlFor="ad-hoc-feedback-body">
              What would you like to say?
            </Label>
            <Textarea
              id="ad-hoc-feedback-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
            />
          </div>
          <div>
            <Label className="mb-1.5 block" htmlFor="ad-hoc-feedback-project">
              Is this about a specific project? (optional)
            </Label>
            {project ? (
              <div className="flex items-center justify-between rounded-md border border-border p-2">
                <span className="text-sm">{project.projectName}</span>
                <Button variant="ghost" size="sm" onClick={() => setProject(null)}>
                  Change
                </Button>
              </div>
            ) : (
              <>
                <Input
                  id="ad-hoc-feedback-project"
                  placeholder="Search NOS for the project…"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                />
                {projectSearch.trim().length > 0 && (
                  <ul className="mt-2 max-h-40 divide-y divide-border overflow-y-auto rounded-md border border-border">
                    {projectsLoading ? (
                      <li className="p-2 text-muted-foreground text-sm">Searching…</li>
                    ) : (projectResults ?? []).length === 0 ? (
                      <li className="p-2 text-muted-foreground text-sm">No projects match.</li>
                    ) : (
                      (projectResults ?? []).map((p) => (
                        <li key={p.projectId}>
                          <button
                            type="button"
                            className="w-full p-2 text-left text-sm hover:bg-muted/40"
                            onClick={() => {
                              setProject(p);
                              setProjectSearch("");
                            }}
                          >
                            {p.projectName}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </>
            )}
          </div>
          <Label className="flex items-center gap-2 font-normal text-sm">
            <Checkbox
              checked={isAnonymous}
              onCheckedChange={(checked) => setIsAnonymous(checked === true)}
            />
            Submit this anonymously
          </Label>
        </div>
        <DialogFooter>
          <Button
            disabled={!canSubmit || give.isPending}
            onClick={() => {
              if (!subjectPersonId || !sentimentKey) return;
              give.mutate(
                {
                  subjectPersonId,
                  sentimentKey,
                  body: body.trim(),
                  isAnonymous,
                  relatedNosProjectId: project?.projectId,
                  relatedProjectName: project?.projectName,
                },
                { onSuccess: () => setOpen(false) },
              );
            }}
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
